import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Heading, 
  Text, 
  View, 
  Button,
  Loader,
  Divider,
  SelectField
} from '@aws-amplify/ui-react';
import { API } from 'aws-amplify';
import { FaBars, FaArrowUp } from 'react-icons/fa';
import "../js/loader.js";
import "../js/appConstants.js";

function FileViewer() {
  const { folder, language, fileName } = useParams();
  const navigate = useNavigate();
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [translationType, setTranslationType] = useState('');
  const [translatedContent, setTranslatedContent] = useState('');
  const [translating, setTranslating] = useState(false);
  const [otherLanguages, setOtherLanguages] = useState([]);
  const [showLangPopover, setShowLangPopover] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [showQuestionPopup, setShowQuestionPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [selectedMeaning, setSelectedMeaning] = useState('');
  const [processingSelected, setProcessingSelected] = useState(false);
  const [actionBarPosition, setActionBarPosition] = useState(null);
  const fileContentRef = React.useRef();
  const hamburgerRef = React.useRef();

  // Set up window load event and call immediately if already loaded
  useEffect(() => {
    const handleLoad = () => {
      window.clickLog("load", "fileviewer");            
    };
    
    // If document is already loaded, call the function immediately
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }
    
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);
  
  // Fetch file content when folder/language/fileName changes
  useEffect(() => {
    fetchFileContent();
    // Reset translation when file changes
    setTranslationType('');
    setTranslatedContent('');
  }, [folder, language, fileName]); // fetchFileContent is defined inline, so it's safe

  // Fetch all language variants for the same song
  useEffect(() => {
    const fetchOtherLanguages = async () => {
      try {
        const fileListResponse = await API.get('NamapagesAPI', 'files', {
          headers: { 'Accept': 'application/json' }
        });
        let allFiles = Array.isArray(fileListResponse)
          ? fileListResponse
          : (fileListResponse && Array.isArray(fileListResponse.files) ? fileListResponse.files : []);
        // Find all files with the same base file name (ignore folder/language)
        const decodedFileName = decodeURIComponent(fileName);
        const baseName = formatFileName(decodedFileName);
        const variants = allFiles.filter(f => formatFileName(f.file_name) === baseName);
        setOtherLanguages(variants);
      } catch (err) {
        setOtherLanguages([]);
      }
    };
    fetchOtherLanguages();
  }, [fileName]);

  const [isContentAvailable, setIsContentAvailable] = useState(true);
  const [songNo, setSongNo] = useState(null);
  
  const fetchFileContent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Construct the file path for API request
      const decodedFileName = decodeURIComponent(fileName);
      
      console.log('Fetching file:', {
        folder,
        language,
        fileName: decodedFileName
      });
      
      // Call the API to get file content
      try {
        // Get the current authenticated session
        // Check if we have s3_path in the file data
        const fileListResponse = await API.get('NamapagesAPI', 'files', {
          headers: {
            'Accept': 'application/json'
          }
        });
        
        // Find the file in the response
        let fileData = null;
        if (Array.isArray(fileListResponse)) {
          fileData = fileListResponse.find(file => 
            file.folder === folder && 
            file.language === language && 
            file.file_name === decodedFileName
          );
        } else if (fileListResponse && Array.isArray(fileListResponse.files)) {
          // Handle new response structure with files property
          fileData = fileListResponse.files.find(file => 
            file.folder === folder && 
            file.language === language && 
            file.file_name === decodedFileName
          );
        }
        
        // Store song_no if available
        if (fileData && fileData.song_no) {
          setSongNo(fileData.song_no);
        }
        
        let response;
        
        if (fileData && fileData.s3_path) {
          // Use the direct S3 path if available
          response = await API.post('NamapagesAPI', 'file', {
            body: {
              s3_path: fileData.s3_path
            }
          });
        } else {
          // Fallback to traditional path
          response = await API.post('NamapagesAPI', 'file', {
            body: {
              file_name: decodedFileName,
              language: `${folder}/${language}`
            }
          });
        }
        
        // Check if content starts with "NOT AVAILABLE IN"
        const isAvailable = typeof response === 'string' && !response.trim().startsWith('NOT AVAILABLE IN');
        setIsContentAvailable(isAvailable);
        
        setFileContent(response);
        console.log('File content loaded successfully');
      } catch (apiError) {
        console.error('API error:', apiError);
        
        // Fallback to mock content if API fails
        let mockContent;
        
        if (folder === "Thirumurai" && language === "TAM" && fileName === "01_Thevaram.txt") {
          mockContent = "திருஞானசம்பந்தர் அருளிய தேவாரம்\n\nபதிகம் 1\n\nதோடுடைய செவியன் விடையேறிய கோன்\nநீடு மதில் அரண் இலங்கை தன்னை\nமூடிய வேலைகள் எழுதரு காலை\nஓடிய ஆறு உடையான் பொன் அடிக்கே";
        } else if (folder === "Thirumurai" && language === "ENG" && fileName === "01_Thevaram.txt") {
          mockContent = "Thevaram by Thirugnana Sambandar\n\nHymn 1\n\nThe Lord who has flower-adorned ears, who rides the bull,\nWho burnt the fortified city of Lanka,\nWhen the seas that covered it rose up,\nWho has the river (Ganga) in his matted hair - at his golden feet";
        } else if (folder === "Divyaprabandham" && language === "TAM" && fileName === "01_Nalayira.txt") {
          mockContent = "நாலாயிர திவ்யப் பிரபந்தம்\n\nதிருப்பாவை\n\nமார்கழித் திங்கள் மதி நிறைந்த நன்னாளால்\nநீராடப் போதுவீர் போதுமினோ நேரிழையீர்\nசீர் மல்கும் ஆய்ப்பாடிச் செல்வச் சிறுமீர்காள்";
        } else if (folder === "Bhagavad Gita" && language === "SAN" && fileName === "01_Chapter1.txt") {
          mockContent = "श्रीमद्भगवद्गीता\n\nअध्याय 1\n\nधृतराष्ट्र उवाच\nधर्मक्षेत्रे कुरुक्षेत्रे समवेता युयुत्सवः।\nमामकाः पाण्डवाश्चैव किमकुर्वत सञ्जय।।1.1।।";
        } else if (folder === "Bhagavad Gita" && language === "ENG" && fileName === "01_Chapter1.txt") {
          mockContent = "Bhagavad Gita\n\nChapter 1\n\nDhritarashtra said:\nO Sanjaya, what did my sons and the sons of Pandu do,\nwhen they assembled on the holy field of Kurukshetra, eager for battle?";
        } else if (folder === "Upanishads" && language === "SAN" && fileName === "01_Isha.txt") {
          mockContent = "ईशावास्योपनिषद्\n\nॐ पूर्णमदः पूर्णमिदं पूर्णात्पूर्णमुदच्यते\nपूर्णस्य पूर्णमादाय पूर्णमेवावशिष्यते॥";
        } else {
          mockContent = `Sample content for ${folder}/${language}/${fileName}\n\nThis is mock content for demonstration purposes.\nThe actual content would be fetched from the API in a production environment.`;
        }
        
        setFileContent(mockContent);
        console.log('Using mock content for file:', decodedFileName);
      }
      
      console.log('Using mock content for file:', decodedFileName);
    } catch (err) {
      console.error('Error fetching file content:', err);
      setError('Failed to fetch file content. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Hide action bar on scroll or click elsewhere
  useEffect(() => {
    const handleScrollOrClick = () => {
      setActionBarPosition(null);
      setSelectedText('');
    };
    window.addEventListener('scroll', handleScrollOrClick);
    window.addEventListener('mousedown', handleScrollOrClick);
    return () => {
      window.removeEventListener('scroll', handleScrollOrClick);
      window.removeEventListener('mousedown', handleScrollOrClick);
    };
  }, []);

  // Handler for mouseup in file content area
  const handleMouseUp = (e) => {
    const selection = window.getSelection();
    const text = selection && selection.toString().trim();
    if (
      text &&
      fileContentRef.current &&
      fileContentRef.current.contains(selection.anchorNode)
    ) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setActionBarPosition({
        x: rect.left + window.scrollX + rect.width / 2,
        y: rect.top + window.scrollY - 40 // above selection
      });
      setSelectedText(text);
    } else {
      setActionBarPosition(null);
      setSelectedText('');
    }
  };

  // Helper to format language code
  const languageCodeMap = {
    'ENG': 'English',
    'TAM': 'Tamil',
    'IAST': 'English (IAST)',
    'SAN': 'Sanskrit',
    'MIX': 'Mixed',
    'DEV': 'Devanagari',
    'ROM': 'Roman'
  };

  // Helper to format file name (remove number prefix and .txt suffix)
  const formatFileName = (fileName) => fileName.replace(/^\d+[\s_-]*/, '').replace(/\.txt$/, '');

  // Compute language display label outside of JSX
  let languageDisplay = '';
  if (language) {
    if (languageCodeMap[language]) {
      languageDisplay = languageCodeMap[language];
    } else {
      for (const [code, name] of Object.entries(languageCodeMap)) {
        if (language.includes(code)) {
          languageDisplay = name;
          break;
        }
      }
      if (!languageDisplay) {
        languageDisplay = language.charAt(0).toUpperCase() + language.slice(1);
      }
    }
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return <Loader size="large" />;
  }

  if (error) {
    return (
      <View>
        <Text variation="error">{error}</Text>
        <Button as={Link} to="/">Back to File List</Button>
      </View>
    );
  }

  const handleTranslate = async (selectedType) => {
    // Use the passed type parameter if provided, otherwise use state
    const typeToUse = selectedType || translationType;
    
    if (!typeToUse || !fileContent) return;
    
    setTranslating(true);
    try {
      console.log(`Translating with type: ${typeToUse}`);
      const response = await API.post('NamapagesAPI', 'translate', {
        body: {
          text: fileContent,
          translationType: typeToUse
        }
      });
      
      if (response && response.output) {
        setTranslatedContent(response.output);
      } else {
        setError('Translation failed. Invalid response format.');
      }
    } catch (err) {
      console.error('Translation error:', err);
      setError('Failed to translate content. Please try again later.');
    } finally {
      setTranslating(false);
    }
    window.clickLog("Translating with type: " + typeToUse, "fileviewer");  
  };

  // Handler for "Sanskrit Meaning" action bar click
  const handleSelectedTranslate = async (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!selectedText) return;
    setProcessingSelected(true);
    setSelectedMeaning('');
    setError(null);
    try {
      const response = await API.post('NamapagesAPI', 'translate', {
        body: {
          text: selectedText,
          translationType: 'provide_sanskrit_meaning'
        }
      });
      if (response && response.output) {
        setSelectedMeaning(response.output);
        setActionBarPosition(null); // Hide the button after successful API call
        setSelectedText('');        // Also clear selection so user can select again
      } else {
        setError('Meaning fetch failed. Invalid response format.');
      }
    } catch (err) {
      setError('Failed to fetch meaning. Please try again later.');
    } finally {
      setProcessingSelected(false);
      if (window.getSelection) window.getSelection().removeAllRanges();
    }
    window.clickLog("Selected text meaning: " + selectedText, "fileviewer");
  };

  return (
    <>
      <View>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <SelectField
                label="Translate"
                value={translationType}
                onChange={(e) => {
                  const newType = e.target.value;
                  setTranslationType(newType);
                  if (newType) {
                    handleTranslate(newType);
                  }
                }}
                isDisabled={!isContentAvailable || translating}
                style={{ 
                  width: '140px', // reduced width
                  fontSize: '0.85rem', // reduced font size
                  borderRadius: '8px',
                  borderColor: '#4CAF50',
                  padding: '2px 6px' // reduced padding
                }}
              >
                <option value="">Translate</option>
                {language !== 'SAN' && (
                  <option value="convert_to_sanskrit">To Sanskrit</option>
                )}
                <option value="convert_from_sanskrit">To English</option>
                <option value="provide_sanskrit_meaning">Meaning</option>
              </SelectField>
              <Text style={{ 
                fontSize: '0.7rem', 
                color: '#FF6D00', 
                fontWeight: 'bold',
                maxWidth: '250px',
                lineHeight: '1.2'
              }}>
                AI-powered translation for learning purposes only. May not be accurate. Especially with Indian context.
              </Text>
            </div>
            {translating && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div className="spinner" style={{ 
                  width: '20px', 
                  height: '20px', 
                  border: '3px solid rgba(0, 0, 0, 0.1)', 
                  borderRadius: '50%', 
                  borderTop: '3px solid #4CAF50', 
                  animation: 'spin 1s linear infinite' 
                }}></div>
                <span style={{ fontSize: '0.9rem', color: '#666' }}>Processing</span>
              </div>
            )}
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                
                select option {
                  background-color: white;
                }
                
                select option:nth-child(1) {
                  background-color: #f8f9fa;
                  color: #333;
                }
                
                select option:nth-child(2) {
                  background-color: #E8F5E9;
                  color: #2E7D32;
                }
                
                select option:nth-child(3) {
                  background-color: #E3F2FD;
                  color: #1565C0;
                }
                
                select option:nth-child(4) {
                  background-color: #FFF3E0;
                  color: #E65100;
                }
              `}
            </style>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Button 
              onClick={() => {
                navigate('/', { 
                  state: { 
                    backFromFile: true,
                    folder: folder,
                    language: language,
                    fileName: decodeURIComponent(fileName)
                  }
                });
              }}
              backgroundColor="#FF9800"
              color="white"
              borderRadius="50%"
              padding="0.5rem"
              _hover={{ 
                backgroundColor: "#F57C00",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
              }}
              style={{
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "2.5rem",
                height: "2.5rem"
              }}
              title="Back"
            >
              <span style={{ fontSize: '1.2rem' }}>⬅️</span>
            </Button>
            <Button 
              as={Link} 
              to="/" 
              backgroundColor="#4CAF50"
              color="white"
              borderRadius="50%"
              padding="0.5rem"
              _hover={{ 
                backgroundColor: "#45a049",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
              }}
              style={{
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "2.5rem",
                height: "2.5rem"
              }}
              title="Home"
            >
              <span style={{ fontSize: '1.2rem' }}>🏠</span>
            </Button>
          </div>
        </div>
        
        <Card 
          padding="1.5rem"
          borderRadius="8px"
          boxShadow="0 4px 8px rgba(0,0,0,0.1)"
          backgroundColor="#fff"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Heading level={3} style={{ margin: 0, color: '#5D4037', fontSize: '1rem' }}>
                {decodeURIComponent(fileName).replace(/^\d+[\s_-]*/, '').replace(/\.txt$/, '')}
              </Heading>
              {songNo && (
                <div style={{ 
                  backgroundColor: '#E3F2FD', 
                  padding: '3px 8px', 
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  color: '#1976D2'
                }}>
                  Song No: {songNo}
                </div>
              )}
            </div>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              fontSize: '0.8rem',
              backgroundColor: '#f8f9fa',
              padding: '8px 12px',
              borderRadius: '16px',
              alignItems: 'center'
            }}>
              <span style={{ fontWeight: 'bold', color: '#666' }}>
                {folder.charAt(0).toUpperCase() + folder.slice(1).replace(/_/g, ' ')}
              </span>
              <span style={{ color: '#999' }}>/</span>
              <span style={{ fontWeight: 'bold', color: '#666' }}>
                {languageDisplay}
              </span>
            </div>
          </div>
          {/* Hamburger icon for language popover moved to far right on its own line */}
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem', marginRight: 0 }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Button
                ref={hamburgerRef}
                onClick={() => setShowLangPopover((v) => !v)}
                backgroundColor="#1976D2"
                color="white"
                borderRadius="50%"
                padding="0.5rem"
                style={{ width: '2.5rem', height: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '0.25rem' }}
                title="Other Languages"
              >
                <FaBars size={20} />
              </Button>
              {showLangPopover && (
                <div
                  style={{
                    position: 'absolute',
                    top: '110%',
                    right: 0,
                    zIndex: 1000,
                    background: '#fff',
                    border: '1px solid #eaeaea',
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    minWidth: '260px',
                    padding: '0.5rem 0',
                  }}
                >
                  {otherLanguages.length === 0 && (
                    <div style={{ padding: '0.5rem 1rem', color: '#888' }}>No other languages found</div>
                  )}
                  {otherLanguages.map((variant) => {
                    const langLabel = languageCodeMap[variant.language] || variant.language;
                    const display = `${langLabel}: ${variant.song_no} - ${formatFileName(variant.file_name)}`;
                    const isCurrent =
                      variant.folder === folder &&
                      variant.language === language &&
                      variant.file_name === decodeURIComponent(fileName);
                    const to = `/file/${variant.folder}/${variant.language}/${encodeURIComponent(variant.file_name)}`;
                    return isCurrent ? (
                      <div
                        key={variant.folder + variant.language + variant.file_name}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#E3F2FD',
                          color: '#1976D2',
                          fontWeight: 'bold',
                          cursor: 'default',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.95rem',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {display}
                      </div>
                    ) : (
                      <Link
                        key={variant.folder + variant.language + variant.file_name}
                        to={to}
                        style={{
                          textDecoration: 'none',
                          color: '#333',
                        }}
                        onClick={() => setShowLangPopover(false)}
                      >
                        <div
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'transparent',
                            fontWeight: 'normal',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '0.95rem',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {display}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Card>
        <Divider style={{ margin: '1rem 0', backgroundColor: '#eaeaea' }} />
        <View
          ref={fileContentRef}
          backgroundColor="#f8f9fa"
          padding="1.5rem"
          borderRadius="8px"
          whiteSpace="pre-wrap"
          fontFamily="'Fira Code', 'Consolas', monospace"
          style={{ 
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
            fontSize: '0.95rem',
            lineHeight: '1.5',
            maxHeight: translatedContent ? '35vh' : '70vh',
            overflowY: 'auto',
            border: '1px solid #eaeaea',
            position: 'relative'
          }}
          onMouseUp={handleMouseUp}
          onContextMenu={e => e.preventDefault()} // Prevent default context menu
        >
          {fileContent}
        </View>
        {/* Floating action bar for selected text */}
        {selectedText && actionBarPosition && (
          <div
            style={{
              position: 'absolute',
              left: actionBarPosition.x,
              top: actionBarPosition.y,
              zIndex: 2000,
              transform: 'translate(-50%, 0)',
              background: '#1976D2',
              color: '#fff',
              borderRadius: '18px',
              padding: '0.3rem 1rem',
              boxShadow: '0 2px 8px rgba(25,118,210,0.18)',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              userSelect: 'none',
              pointerEvents: 'auto'
            }}
            tabIndex={0}
            role="button"
            onMouseDown={e => {
              // Use onMouseDown instead of onClick to bypass possible focus/blur issues
              handleSelectedTranslate(e);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSelectedTranslate(e);
              }
            }}
            title="Get Sanskrit meaning"
          >
            {processingSelected ? (
              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: '18px',
                    height: '18px',
                    border: '3px solid #fff',
                    borderTop: '3px solid #1976D2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '8px',
                    verticalAlign: 'middle'
                  }}
                ></span>
                Processing...
                <style>
                  {`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}
                </style>
              </span>
            ) : (
              <>
                <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>🔍</span>
                Sanskrit Meaning
              </>
            )}
          </div>
        )}
        {/* Processing spinner for selected text meaning */}
        {(processingSelected) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '1rem' }}>
            <div className="spinner" style={{ 
              width: '20px', 
              height: '20px', 
              border: '3px solid rgba(0, 0, 0, 0.1)', 
              borderRadius: '50%', 
              borderTop: '3px solid #4CAF50', 
              animation: 'spin 1s linear infinite' 
            }}></div>
            <span style={{ fontSize: '0.9rem', color: '#666' }}>Processing</span>
          </div>
        )}
        
        {translatedContent && !translating && (
          <>
            <Divider style={{ margin: '1.5rem 0', backgroundColor: '#eaeaea' }} />
            <Heading level={4} style={{ margin: '1rem 0', color: '#333' }}>
              Translation Result
            </Heading>
            <View
              backgroundColor="#FFF1F0"
              padding="1.5rem"
              borderRadius="8px"
              whiteSpace="pre-wrap"
              fontFamily="'Fira Code', 'Consolas', monospace"
              style={{ 
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                fontSize: '0.95rem',
                lineHeight: '1.5',
                maxHeight: '35vh',
                overflowY: 'auto',
                border: '1px solid #FFCDD2'
              }}
            >
              {translatedContent}
            </View>
          </>
        )}
        {/* Show selected text meaning result */}
        {selectedMeaning && !processingSelected && (
          <>
            <Divider style={{ margin: '1.5rem 0', backgroundColor: '#eaeaea' }} />
            <Heading level={4} style={{ margin: '1rem 0', color: '#333' }}>
              Meaning for Selected Text
            </Heading>
            <View
              backgroundColor="#FFF1F0"
              padding="1.5rem"
              borderRadius="8px"
              whiteSpace="pre-wrap"
              fontFamily="'Fira Code', 'Consolas', monospace"
              style={{ 
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                fontSize: '0.95rem',
                lineHeight: '1.5',
                maxHeight: '35vh',
                overflowY: 'auto',
                border: '1px solid #FFCDD2'
              }}
            >
              {selectedMeaning}
            </View>
          </>
        )}
      </View>
      {/* Always show scroll-to-top button for testing, outside main View */}
      <Button
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          right: '2.5rem', // moved further to the left
          width: '2.4rem',
          height: '2.2rem',
          backgroundColor: 'rgba(25, 118, 210, 0.7)',
          color: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.3rem',
          transition: 'background 0.2s',
          backdropFilter: 'blur(2px)',
          maxWidth: '100vw',
          overflow: 'hidden',
        }}
        title="Scroll to top"
        onMouseOver={e => e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.95)'}
        onMouseOut={e => e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.7)'}
        onClick={scrollToTop}
      >
        <FaArrowUp style={{ fontSize: '1.3rem', color: 'white' }} />
      </Button>
    </>
  );
}

export default FileViewer;